/**
 * Ready-made {single brace} AI prompts for the template editor.
 *
 * Static {{variables}} pull a DB field as-is. These snippets insert a short
 * instruction the per-lead AI resolves later — the full prompt is visible in
 * the textarea so the user can tweak it after clicking the chip.
 */

/**
 * Wrap a prompt in single braces. Already-wrapped text is left alone so a
 * stored snippet never becomes `{{prompt}}`.
 *
 * @param {string} prompt
 * @returns {string}
 */
export function wrapSnippet(prompt) {
  const trimmed = String(prompt || '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    return trimmed;
  }
  return `{${trimmed}}`;
}

/**
 * Text that lands in the editor when a snippet chip is clicked.
 *
 * @param {{ prompt?: string }} snippet
 * @returns {string}
 */
export function snippetInsertText(snippet) {
  return wrapSnippet(snippet?.prompt);
}

export const DYNAMIC_SNIPPETS = [
  {
    id: 'cleaned_name',
    label: 'Cleaned Name',
    icon: 'fa-wand-magic-sparkles',
    description: 'Company name without legal suffixes or ALL CAPS, written so it sits naturally in a sentence.',
    prompt: 'Name des Unternehmens ohne "GmbH" oder All-Caps-Schreibweise, menschlich geschrieben. Streiche rechtliche Zusätze, staatliche Kürzel und Amtsteile und gib nur die kurze Form aus, die man im Satz wirklich sagen würde.',
  },
  {
    id: 'informal_first_name',
    label: 'Informal First Name',
    icon: 'fa-user',
    description: 'Just the first name, written the way it would appear in a greeting.',
    prompt: 'Vorname des Ansprechpartners, nur der Vorname, natürlich geschrieben',
  },
  {
    id: 'what_they_do',
    label: 'What they do',
    icon: 'fa-briefcase',
    description: 'A short, natural phrase for what the company actually does.',
    prompt: 'Was das Unternehmen macht, in einem kurzen natürlichen Halbsatz',
  },
  {
    id: 'dream_audience',
    label: 'Dream Audience',
    icon: 'fa-crosshairs',
    description: "The target's ideal customer — one concrete group that sits naturally in a sentence.",
    prompt: 'Die Traumzielgruppe dieses Unternehmens, aus Web Research oder Beschreibung. So konkret und kurz, wie man sie im Satz nennen würde — eine Gruppe, keine Liste und keine Floskeln.',
  },
  {
    id: 'typical_pain',
    label: 'Typical Pain',
    icon: 'fa-bolt',
    description: 'A specific problem this company likely has, not a generic industry cliché.',
    prompt: 'Ein konkretes Problem, das dieses Unternehmen sehr wahrscheinlich hat, aus Branche und Web Research. Ein kurzer natürlicher Halbsatz, spezifisch für sie, nicht generisch.',
  },
  {
    id: 'concrete_offer',
    label: 'Concrete Offer',
    icon: 'fa-tag',
    description: 'Their main product or offer, shortened to a few words you would actually say.',
    prompt: 'Das Hauptangebot oder ein konkretes Produkt dieses Unternehmens, aus Web Research oder Beschreibung. Auf 2–3 Wörter runterbrechen, so wie man es im Satz nennen würde.',
  },
];

export default DYNAMIC_SNIPPETS;
